/**
 * Legacy ANSI Bangla (Bijoy) → Unicode, for imported artwork.
 *
 * A Bijoy-era file contains no Bangla at all as far as a computer is concerned:
 * SutonnyMJ and its family are 8-bit LATIN fonts whose glyphs merely look Bangla,
 * so the bytes in the PDF/PSD spell `RyZv wb‡P` and only that exact font makes
 * them read `জুতা নিচে`. Imported verbatim, the editor (which has no such font,
 * and shouldn't ship one) shows the Latin gibberish.
 *
 * So anything coming from such a font is transliterated to real Unicode Bangla and
 * re-typed in a Unicode Bangla family: the text then reads correctly, stays
 * editable and searchable, and exports the same everywhere.
 *
 * The converter is the very one the site's own Bijoy ⇄ Unicode tool uses, so an
 * import and that tool can never disagree.
 */
import { bijoyToUnicode } from '@abdalgolabs/ansi-unicode-converter';

const BANGLA = /[\u0980-\u09ff]/;

/** Fonts that carry Bijoy/ANSI Bangla — the "MJ" family plus the named classics. */
const LEGACY_FONT = /(^|[\s+_-])(sutonny|boishakhi|amar\s*bangla|bijoy|proshika|shreelipi|modhumati|chandrabati|lekhoni)|mj\b/i;

/** Unicode Bangla faces, which must never be run through the converter. */
const UNICODE_FONT = /(solaimanlipi|kalpurush|siyam|nikosh|noto|hind|baloo|anek|tiro|mukti|akaash|likhan|shobhika|galada)/i;

/** True for a font whose bytes are Bijoy-encoded rather than Unicode. */
export function isLegacyBanglaFont(name) {
    const n = String(name || '').replace(/^[A-Z]{6}\+/, '').trim();
    if (!n) return false;
    if (UNICODE_FONT.test(n)) return false;
    return LEGACY_FONT.test(n);
}

/**
 * The signature of Bijoy text when the font name is unavailable — pdf.js hands
 * out `g_d0_f1` for an embedded subset, not `SutonnyMJ`.
 *
 * Bijoy leans on the Windows-1252 punctuation block and Latin-1 accents for its
 * conjuncts (`‡`, `¯`, `Ò`, …), which ordinary English copy simply does not
 * contain. Deliberately strict: mistaking English for Bijoy would SCRAMBLE it,
 * so leaving text alone is by far the cheaper mistake.
 */
const BIJOY_SIGNATURE = /[\u0080-\u00ff\u2018\u2019\u201c\u201d\u2020\u2021\u2026\u2030\u2039\u203a\u0152\u0153\u0160\u0161\u0178\u017d\u017e\u0192\u02c6\u02dc]/;

export function looksLikeLegacyBangla(text) {
    const s = String(text || '');
    if (!s.trim() || BANGLA.test(s)) return false; // already Unicode Bangla
    return BIJOY_SIGNATURE.test(s);
}

/** How many signature characters a string carries — one accent is an accented
 *  Latin word, a handful is Bijoy. */
export function legacySignatureCount(text) {
    let n = 0;
    for (const ch of String(text || '')) if (BIJOY_SIGNATURE.test(ch)) n += 1;
    return n;
}

/**
 * A run that is Bijoy beyond reasonable doubt: it carries the signature AND
 * converts into text that is overwhelmingly Bangla. Used only when the font name
 * is unavailable — `café` must not be "converted" into nonsense.
 */
export function isLegacyBanglaRun(text) {
    const s = String(text || '');
    if (!looksLikeLegacyBangla(s)) return false;
    const out = legacyBanglaToUnicode(s);
    if (out === s) return false;
    const letters = out.replace(/[\s\d\p{P}\p{S}]/gu, '');
    if (!letters) return false;
    const bangla = (letters.match(/[\u0980-\u09ff]/g) || []).length;
    return bangla / letters.length >= 0.8;
}

/**
 * Convert a run, keeping the result ONLY when it really came out as Bangla —
 * a mis-detected Latin string would otherwise be turned into nonsense.
 */
export function legacyBanglaToUnicode(text) {
    const s = String(text ?? '');
    if (!s) return s;
    try {
        const out = bijoyToUnicode(s);
        return out && BANGLA.test(out) ? out : s;
    } catch {
        return s;
    }
}

/**
 * The Unicode family that stands in for a legacy face. Sutonny and friends are
 * traditional serif Bangla types, so they read closest in Noto Serif Bengali;
 * everything else takes the editor's default Bangla sans.
 */
export function legacyBanglaFamily(name) {
    return /sutonny|modhumati|chandrabati|serif/i.test(String(name || ''))
        ? 'Noto Serif Bengali'
        : 'Hind Siliguri';
}

/**
 * Would converting this text produce real Bangla? Looser than the automatic test
 * (no signature required) because the caller is a person clicking "convert", not
 * a heuristic guessing: a Bijoy headline's second line is often pure ASCII
 * (`ivLyb!` = রাখুন!) and must still be offered the repair.
 */
export function couldBeLegacyBangla(text) {
    const s = String(text || '');
    if (!s.trim() || BANGLA.test(s)) return false;
    const out = legacyBanglaToUnicode(s);
    if (out === s) return false;
    const letters = out.replace(/[\s\d\p{P}\p{S}]/gu, '');
    if (!letters) return false;
    return (letters.match(/[\u0980-\u09ff]/g) || []).length / letters.length >= 0.8;
}
